import React from "react";
import { Tag } from "antd";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
dayjs.extend(utc);
dayjs.extend(timezone);
import { SOURCE_COLORS, STATUS_COLORS, formatCurrency, describePaymentChannel } from "./salesDashboardUtils";

// ============================================================
// Tab 1: Daily Sales Report - Unified Sales Grid
// ============================================================
export const getDailySalesColumns = () => [
  {
    headerName: "Source",
    field: "source",
    width: 130,
    pinned: "left",
    cellRenderer: (params) => {
      const color = SOURCE_COLORS[params.value] || "#666";
      return <Tag color={color}>{params.value}</Tag>;
    },
  },
  {
    headerName: "Brand",
    field: "client",
    width: 140,
  },
  {
    headerName: "Order ID",
    field: "order_id",
    width: 160,
  },
  {
    headerName: "Transaction ID",
    field: "transaction_id",
    width: 160,
  },
  {
    headerName: "Customer",
    field: "customer_name",
    width: 180,
  },
  {
    headerName: "Email",
    field: "customer_email",
    width: 220,
  },
  {
    headerName: "Amount",
    field: "amount",
    width: 130,
    cellStyle: { textAlign: "right" },
    valueFormatter: (params) => formatCurrency(params.value),
    filter: "agNumberColumnFilter",
  },
  {
    headerName: "Payment Type",
    field: "payment_type",
    width: 130,
    headerTooltip: "Cash, Check, or Gateway — based on the order payment method, not whether a gateway row later matched.",
    valueGetter: (params) => describePaymentChannel(params.data),
  },
  {
    headerName: "Status",
    field: "status",
    width: 120,
    cellRenderer: (params) => {
      const color = STATUS_COLORS[params.value] || "default";
      return <Tag color={color}>{params.value}</Tag>;
    },
  },
  {
    headerName: "Date",
    field: "date",
    width: 160,
    sort: "desc",
    valueFormatter: (params) => {
      if (!params.value) return "";
      return dayjs.utc(params.value).tz("America/New_York").format("YYYY-MM-DD HH:mm");
    },
    comparator: (a, b) => {
      if (!a) return -1;
      if (!b) return 1;
      return new Date(a) - new Date(b);
    },
  },
  {
    headerName: "Sublytics Match",
    field: "_matched_sub_order_id",
    width: 155,
    headerTooltip: "For gateway / TigerTech rows: the Sublytics order this record was reconciled against.",
    cellRenderer: (params) => {
      const src = params.data?.source;
      if (src === "Sublytics" || src === "Wordpress") return <Tag color="cyan">Source</Tag>;
      if (["Check", "Cash"].includes(src)) {
        const oid = params.data?.order_id;
        return oid ? <Tag color="green" style={{ fontSize: 10 }}>Sub #{oid}</Tag> : <Tag color="default">N/A</Tag>;
      }
      if (src === "RPM" || src === "NexRep") {
        const sid = params.data?._matched_sub_order_id;
        return sid
          ? <Tag color="green" style={{ fontSize: 10 }}>Sub #{sid}</Tag>
          : <Tag color="orange">Unmatched</Tag>;
      }
      const orderId = params.value;
      if (orderId) {
        return (
          <Tag color="green" style={{ fontSize: 10 }}>
            {orderId}
          </Tag>
        );
      }
      return <Tag color="orange">Unmatched</Tag>;
    },
  },
  {
    headerName: "TigerTech Match",
    field: "matched_tigertech",
    width: 140,
    headerTooltip:
      "Sublytics / WordPress: TigerTech link (DB or same-brand + order/txn id). NMI / Auth.Net: TigerTech link only when txn/order ids match on the same brand. Check/Cash: N/A.",
    cellRenderer: (params) => {
      const src = params.data?.source;
      if (src === "TigerTech") return <Tag color="cyan">Source</Tag>;
      if (["Reliable Solutions", "RPM", "NexRep"].includes(src)) {
        return <Tag color="default">N/A</Tag>;
      }
      if (["Check", "Cash"].includes(src)) {
        return params.data?.rawData?.respective_tiger_tech_order
          ? <Tag color="green">Matched</Tag>
          : <Tag color="red">No Match</Tag>;
      }
      if (["AUTH.NET #1", "AUTH.NET #2", "NMI Payarc", "Stripe"].includes(src)) {
        return params.value ? (
          <Tag color="green">Matched</Tag>
        ) : (
          <Tag color="red">No Match</Tag>
        );
      }
      return params.value ? (
        <Tag color="green">Matched</Tag>
      ) : (
        <Tag color="red">No Match</Tag>
      );
    },
  },
  {
    headerName: "Gateway Match",
    field: "matched_gateway",
    width: 140,
    headerTooltip:
      "Sublytics / WordPress: payment gateway data (DB or inferred from this grid). TigerTech: inferred NMI/Auth row. Check/Cash: N/A.",
    cellRenderer: (params) => {
      const src = params.data?.source;
      if (["Reliable Solutions", "RPM", "NexRep"].includes(src)) {
        return <Tag color="default">N/A</Tag>;
      }
      if (["Check", "Cash"].includes(src)) {
        return <Tag color="geekblue">Offline</Tag>;
      }
      if (["AUTH.NET #1", "AUTH.NET #2", "NMI Payarc", "Stripe", "Elavon"].includes(src)) {
        return <Tag color="cyan">Source</Tag>;
      }
      if (src === "TigerTech") {
        return params.value ? (
          <Tag color="green">Matched</Tag>
        ) : (
          <Tag color="orange">Unmatched</Tag>
        );
      }
      return params.value ? (
        <Tag color="green">Matched</Tag>
      ) : (
        <Tag color="orange">Unmatched</Tag>
      );
    },
  },
  {
    headerName: "Matched Via",
    field: "reconciliation_gateway_id",
    width: 240,
    headerTooltip: "Sublytics/WP: which gateway matched (with txn IDs). Gateway/TT: which Sublytics order matched.",
    cellRenderer: (params) => {
      const src = params.data?.source;
      const tagColor = { "AUTH.NET": SOURCE_COLORS["AUTH.NET #1"], NMI: SOURCE_COLORS["NMI Payarc"], Stripe: SOURCE_COLORS.Stripe, Reliable: SOURCE_COLORS["Reliable Solutions"], Elavon: SOURCE_COLORS.Elavon || "#1d4ed8", TigerTech: SOURCE_COLORS.TigerTech, Gateway: "#1890ff" };
      if (["Sublytics", "Wordpress"].includes(src)) {
        const raw = params.data?.rawData || {};
        const parts = [];
        const authTxns = raw.real_auth_transactions || [];
        if (authTxns.length > 0) {
          const ids = authTxns.map((t) => t.transId).filter(Boolean);
          parts.push({ gw: "AUTH.NET", label: ids.length ? `AUTH.NET: ${ids.join(", ")}` : "AUTH.NET" });
        }
        const nmiTxns = raw.real_nmi_transactions || [];
        const nmiHints = raw._dashboard_nmi_hints || [];
        if (nmiTxns.length > 0 || raw._dashboard_linked_nmi) {
          const pool = nmiTxns.length ? nmiTxns : nmiHints;
          const ids = pool.map((t) => t.transaction_id || t.id).filter(Boolean);
          parts.push({ gw: "NMI", label: ids.length ? `NMI: ${ids.join(", ")}` : "NMI" });
        }
        const stripeTxns = raw.real_stripe_transactions || [];
        if (stripeTxns.length > 0) {
          const ids = stripeTxns.map((t) => t.charge_id || t.id).filter(Boolean);
          parts.push({ gw: "Stripe", label: ids.length ? `Stripe: ${ids.join(", ")}` : "Stripe" });
        }
        const reliableTxns = raw.real_reliable_transactions || [];
        if (reliableTxns.length > 0) {
          const ids = reliableTxns.map((t) => t.transaction_id || t.id).filter(Boolean);
          parts.push({ gw: "Reliable", label: ids.length ? `Reliable: ${ids.join(", ")}` : "Reliable" });
        }
        if (parts.length === 0 && params.data?.matched_gateway) {
          const gwSrc = params.data?._matched_gateway_source;
          const gwTxn = params.data?._matched_gateway_txn_id || "";
          const suffix = gwTxn ? `: ${gwTxn}` : "";
          if (gwSrc?.startsWith("AUTH.NET")) parts.push({ gw: "AUTH.NET", label: `AUTH.NET${suffix}` });
          else if (gwSrc === "NMI Payarc") parts.push({ gw: "NMI", label: `NMI${suffix}` });
          else if (gwSrc === "Stripe") parts.push({ gw: "Stripe", label: `Stripe${suffix}` });
          else if (gwSrc === "Reliable Solutions") parts.push({ gw: "Reliable", label: `Reliable${suffix}` });
          else if (gwSrc === "Elavon") parts.push({ gw: "Elavon", label: `Elavon${suffix}` });
          else parts.push({ gw: "Gateway", label: `Gateway${suffix}` });
        }
        if (raw.respective_tiger_tech_order) parts.push({ gw: "TigerTech", label: "TigerTech" });
        if (parts.length === 0) return <Tag color="default">—</Tag>;
        return (
          <span style={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            {parts.map((p, i) => (
              <Tag key={i} color={tagColor[p.gw] || "#666"} style={{ fontSize: 10 }}>{p.label}</Tag>
            ))}
          </span>
        );
      }
      const subId = params.data?._matched_sub_order_id;
      if (subId) {
        return <Tag color="green" style={{ fontSize: 10 }}>Sub #{subId}</Tag>;
      }
      if (["Check", "Cash"].includes(src)) {
        const oid = params.data?.order_id;
        return oid ? <Tag color="green" style={{ fontSize: 10 }}>Sub #{oid}</Tag> : <Tag color="default">N/A</Tag>;
      }
      return <Tag color="default">—</Tag>;
    },
  },
  {
    headerName: "Reconciliation",
    field: "reconciliation_status",
    width: 160,
    cellRenderer: (params) => {
      const src = params.data?.source;
      if (!["Sublytics", "Wordpress"].includes(src)) return null;
      const s = params.value;
      const colorMap = {
        matched: "green",
        matched_with_variance: "gold",
        missing_in_gateway: "red",
        duplicate_candidate: "volcano",
        partially_matched: "orange",
        ambiguous_match: "magenta",
        settled_offline: "geekblue",
        pending: "blue",
      };
      const labelMap = {
        matched: "Matched",
        matched_with_variance: "Variance",
        missing_in_gateway: "Missing",
        duplicate_candidate: "Duplicate",
        partially_matched: "Partial",
        ambiguous_match: "Ambiguous",
        settled_offline: "Offline",
        pending: "Pending",
      };
      return <Tag color={colorMap[s] || "default"}>{labelMap[s] || s || "—"}</Tag>;
    },
  },
];

// ============================================================
// Tab 2: Merged Stock Update - ShipStation Inventory
// Original ShipStation columns + Back Orders & Since When
// ============================================================
export const getStockColumns = () => [
  { headerName: "Client", field: "brand_name", width: 140, pinned: "left", cellRenderer: (params) => <Tag color="blue">{params.value}</Tag> },
  { headerName: "SKU", field: "sku", width: 150 },
  { headerName: "ProductName", field: "name", width: 250, flex: 1 },
  { headerName: "WarehouseName", field: "warehouse", width: 160 },
  { headerName: "Loc1", field: "loc1", width: 100 },
  { headerName: "Loc2", field: "loc2", width: 100 },
  { headerName: "Loc3", field: "loc3", width: 100 },
  { headerName: "Loc4", field: "loc4", width: 100 },
  { headerName: "OnHand", field: "on_hand", width: 110, filter: "agNumberColumnFilter", cellStyle: { textAlign: "right" } },
  { headerName: "ReorderThreshold", field: "reorder_level", width: 150, filter: "agNumberColumnFilter", cellStyle: { textAlign: "right" } },
  { headerName: "LotID", field: "lot_id", width: 120 },
  { headerName: "ExpirationDate", field: "expiration_date", width: 140 },
  {
    headerName: "Back Orders", field: "backorder_count", width: 120, filter: "agNumberColumnFilter",
    cellStyle: (params) => ({
      textAlign: "right",
      color: params.value > 0 ? "#ff4d4f" : undefined,
      fontWeight: params.value > 0 ? 600 : undefined,
    }),
    valueFormatter: (params) => params.value != null && params.value > 0 ? params.value : "",
  },
  {
    headerName: "Since When", field: "backorder_since", width: 130,
    valueFormatter: (params) => params.value ? dayjs(params.value).format("MMM DD, YYYY") : "",
    cellStyle: (params) => ({
      color: params.value ? "#ff4d4f" : undefined,
    }),
  },
];

// ============================================================
// Tab 3: Merged Sales Dump - Product Order Data
// ============================================================
const currencyCol = (headerName, field, width = 120) => ({
  headerName,
  field,
  width,
  filter: "agNumberColumnFilter",
  cellStyle: { textAlign: "right" },
  valueFormatter: (params) => formatCurrency(params.value),
});

export const getSalesDumpColumns = () => [
  {
    headerName: "Brand",
    field: "brand_name",
    width: 130,
    pinned: "left",
    cellRenderer: (params) => <Tag color="blue">{params.value}</Tag>,
  },
  { headerName: "Company", field: "company", width: 130 },
  { headerName: "Purchase Date", field: "purch_date", width: 125, sort: "desc" },
  { headerName: "Order #", field: "order_num", width: 120 },
  { headerName: "Customer ID", field: "customer_id", width: 110 },
  { headerName: "Customer", field: "name", width: 170, flex: 1 },
  { headerName: "Address1", field: "address1", width: 180 },
  { headerName: "Address2", field: "address2", width: 140 },
  { headerName: "City", field: "city", width: 120 },
  { headerName: "State", field: "state", width: 80 },
  { headerName: "Zip", field: "zip", width: 90 },
  { headerName: "Mail Code", field: "mail_code", width: 120 },
  { headerName: "Mail Date", field: "mail_date", width: 115 },
  { headerName: "Offer Code", field: "offer_code", width: 115 },
  { headerName: "Product Code", field: "camp_prod_code", width: 125 },
  { headerName: "Product Desc", field: "camp_prod_desc", width: 170 },
  { headerName: "Category", field: "category", width: 120 },
  { headerName: "Source", field: "source", width: 110 },
  { headerName: "List", field: "list", width: 130 },
  { headerName: "Segment", field: "segment", width: 130 },
  { headerName: "Days Order-Mail", field: "order_date_mail_date", width: 130, filter: "agNumberColumnFilter", cellStyle: { textAlign: "right" } },
  { headerName: "Days Since Order", field: "today_order_date", width: 130, filter: "agNumberColumnFilter", cellStyle: { textAlign: "right" } },
  currencyCol("Order Sale Amt", "order_sale_amt", 135),
  currencyCol("Product Sales", "product_sales_amt", 125),
  currencyCol("Xsell Sales", "xsell_sales_amt", 115),
  currencyCol("Shipping", "shipping", 105),
  currencyCol("Refund Amt", "refund_amount", 115),
  currencyCol("Total Cost", "total_cost", 115),
  currencyCol("Product Cost", "product_cost", 115),
  currencyCol("Ship Cost", "ship_cost", 105),
  currencyCol("Merch Fee", "merch_fee", 105),
  currencyCol("Fulfill Cost", "fulfill_cost", 110),
  currencyCol("Call Center", "callcenter_cost", 115),
  currencyCol("Royalty Amt", "royalty_amt", 110),
  currencyCol("Refund Cost", "refund_cost", 110),
  currencyCol("Phone Sales", "phone_sales", 115),
  currencyCol("Mail Sales", "mail_sales", 110),
  currencyCol("Web Sales", "web_sales", 110),
  currencyCol("Unknown Sales", "unknown_sales", 120),
  { headerName: "Prod Code 1", field: "prodcode1", width: 110 },
  { headerName: "Prod Code 2", field: "prodcode2", width: 110 },
  { headerName: "Prod Code 3", field: "prodcode3", width: 110 },
  { headerName: "Prod Code 4", field: "prodcode4", width: 110 },
  { headerName: "Prod Code 5", field: "prodcode5", width: 110 },
  { headerName: "Prod Code 6", field: "prodcode6", width: 110 },
  { headerName: "Prod Code 7", field: "prodcode7", width: 110 },
  { headerName: "Prod Code 8", field: "prodcode8", width: 110 },
  { headerName: "Prod Code 9", field: "prodcode9", width: 110 },
  { headerName: "Prod Qty 1", field: "prodqty1", width: 100, filter: "agNumberColumnFilter", cellStyle: { textAlign: "right" } },
  { headerName: "Prod Qty 2", field: "prodqty2", width: 100, filter: "agNumberColumnFilter", cellStyle: { textAlign: "right" } },
  { headerName: "Prod Qty 3", field: "prodqty3", width: 100, filter: "agNumberColumnFilter", cellStyle: { textAlign: "right" } },
  { headerName: "Prod Qty 4", field: "prodqty4", width: 100, filter: "agNumberColumnFilter", cellStyle: { textAlign: "right" } },
  { headerName: "Prod Qty 5", field: "prodqty5", width: 100, filter: "agNumberColumnFilter", cellStyle: { textAlign: "right" } },
  { headerName: "Prod Qty 6", field: "prodqty6", width: 100, filter: "agNumberColumnFilter", cellStyle: { textAlign: "right" } },
  { headerName: "Prod Qty 7", field: "prodqty7", width: 100, filter: "agNumberColumnFilter", cellStyle: { textAlign: "right" } },
  { headerName: "Prod Qty 8", field: "prodqty8", width: 100, filter: "agNumberColumnFilter", cellStyle: { textAlign: "right" } },
  { headerName: "Prod Qty 9", field: "prodqty9", width: 100, filter: "agNumberColumnFilter", cellStyle: { textAlign: "right" } },
  currencyCol("Prod Sale 1", "prodsale1", 110),
  currencyCol("Prod Sale 2", "prodsale2", 110),
  currencyCol("Prod Sale 3", "prodsale3", 110),
  currencyCol("Prod Sale 4", "prodsale4", 110),
  currencyCol("Prod Sale 5", "prodsale5", 110),
  currencyCol("Prod Sale 6", "prodsale6", 110),
  currencyCol("Prod Sale 7", "prodsale7", 110),
  currencyCol("Prod Sale 8", "prodsale8", 110),
  currencyCol("Prod Sale 9", "prodsale9", 110),
];

// ============================================================
// Tab 4: Merged Mailing/LTV Response
// ============================================================
const numCol = (headerName, field, width = 100) => ({
  headerName,
  field,
  width,
  filter: "agNumberColumnFilter",
  cellStyle: { textAlign: "right" },
});
const moneyCol = (headerName, field, width = 115) => ({
  headerName,
  field,
  width,
  filter: "agNumberColumnFilter",
  cellStyle: { textAlign: "right" },
  valueFormatter: (params) => formatCurrency(params.value),
});
const pctCol = (headerName, field, width = 100) => ({
  headerName,
  field,
  width,
  filter: "agNumberColumnFilter",
  cellStyle: { textAlign: "right" },
  valueFormatter: (params) => params.value != null ? `${Number(params.value).toFixed(2)}%` : "",
});

export const getMailingLTVColumns = () => [
  { headerName: "Company", field: "company", width: 130, pinned: "left" },
  { headerName: "Group Code", field: "group_code", width: 120 },
  { headerName: "Mailing Code", field: "mailing_code", width: 130 },
  { headerName: "Mlg Desc", field: "mailing_description", width: 200, flex: 1 },
  {
    headerName: "Mail Date",
    field: "mail_date",
    width: 120,
    sort: "desc",
    cellRenderer: (params) => {
      if (!params.value) return "N/A";
      return dayjs(params.value).format("MMM DD, YYYY");
    },
  },
  { headerName: "Offer", field: "offer", width: 130 },
  { headerName: "Offer Desc", field: "offer_description", width: 160 },
  { headerName: "Product", field: "product", width: 100 },
  { headerName: "Product Desc", field: "product_description", width: 160 },
  { headerName: "Category", field: "category", width: 110 },
  { headerName: "Source Code", field: "source_code", width: 110 },
  { headerName: "List", field: "mailing_list", width: 140 },
  { headerName: "Segment", field: "segment", width: 120 },
  numCol("Ship Qty", "shipped_quantity"),
  numCol("Mailed", "mailed_quantity"),
  pctCol("Net %", "net_percentage"),
  moneyCol("Printing", "printing_cost"),
  moneyCol("Lists", "list_cost"),
  moneyCol("Postage", "postage_cost"),
  moneyCol("Lettershop", "lettershop_cost"),
  moneyCol("DP", "dp_cost"),
  moneyCol("Misc", "misc_cost"),
  moneyCol("Tot Mlg Cost", "total_mailing_cost"),
  numCol("Mail Orders", "mail_orders"),
  numCol("Phone Orders", "phone_orders"),
  numCol("Web Orders", "web_orders"),
  numCol("Unk Orders", "unknown_orders"),
  numCol("Gross Orders", "gross_orders"),
  pctCol("Gross %", "gross_percentage"),
  numCol("Net Orders", "net_orders"),
  pctCol("Net %", "net_percentage_orders"),
  pctCol("Loss Rate %", "loss_rate_percentage"),
  numCol("Xsell Orders", "cross_sell_orders"),
  numCol("Refund Orders", "refund_orders"),
  numCol("Subs", "subscriptions"),
  numCol("Active Subs", "active_subscriptions"),
  numCol("Inquirers", "inquiries"),
  numCol("Backorders", "backorders"),
  moneyCol("B/O Amt", "backorder_amount"),
  pctCol("% with B/O", "percentage_with_backorders"),
  moneyCol("Prod Amt", "product_amount"),
  moneyCol("X-Sell Amt", "cross_sell_amount"),
  moneyCol("Misc Amt", "misc_amount"),
  moneyCol("Non CC Amt", "non_credit_card_amount"),
  moneyCol("CC Amt", "credit_card_amount"),
  numCol("Auto-Ships", "auto_ships"),
  moneyCol("Gross Sales", "gross_sales"),
  moneyCol("Phone Sales", "phone_sales"),
  moneyCol("Mail Sales", "mail_sales"),
  moneyCol("Web Sales", "web_sales"),
  moneyCol("Unknown Sales", "unknown_sales"),
  moneyCol("Turnover/Mail (RPP)", "turnover_per_mail", 140),
  moneyCol("Avg Sale Value", "average_sale_value"),
  moneyCol("Refunds", "refunds"),
  pctCol("Refund Rate %", "refund_rate_percentage"),
  moneyCol("Net Sales", "net_sales"),
  moneyCol("Product Cost", "product_cost"),
  moneyCol("Ship Cost", "shipping_cost"),
  moneyCol("Fulfill Cost", "fulfillment_cost"),
  moneyCol("Refund Cost", "refund_cost"),
  moneyCol("Call Ctr", "call_center_cost"),
  moneyCol("Merch Fee", "merchant_fee"),
  moneyCol("Royalties", "royalties"),
  moneyCol("Total Cost", "total_cost"),
  moneyCol("Cost/Mail (CPP)", "cost_per_mail"),
  moneyCol("Net P/L", "net_profit_loss", 120),
  pctCol("Profit %", "profit_percentage"),
  pctCol("Net ROI", "net_roi"),
  pctCol("% Breakeven", "percentage_breakeven"),
  pctCol("Breakeven % Resp", "breakeven_percentage_response", 130),
  moneyCol("Net Per Piece", "net_per_piece"),
  moneyCol("Avg Order", "average_order_value"),
  moneyCol("Avg w/Autoship", "average_with_autoship"),
  numCol("Avg Turns", "average_turns"),
  moneyCol("Mlg Cost", "mailing_cost"),
  moneyCol("Product Cost", "product_cost1"),
  moneyCol("Call Ctr", "call_center_cost1"),
  moneyCol("Merch Fee", "merchant_fee1"),
  moneyCol("Total Cost", "total_cost2"),
  moneyCol("Net PL/Order", "net_profit_loss_per_order"),
  moneyCol("Avg w/Autoship", "average_with_autoship1"),
  moneyCol("Mlg Cost", "mailing_cost1"),
  moneyCol("Product Cost", "product_cost2"),
  moneyCol("Call Ctr", "call_center_cost2"),
  moneyCol("Merch Fee", "merchant_fee2"),
  moneyCol("Total Cost", "total_cost1"),
  moneyCol("Net PL/M", "net_pl_m"),
  numCol("NSF Count", "nsf_count"),
  numCol("Days", "days_count"),
  moneyCol("FE AOV", "first_event_aov"),
  moneyCol("BE AOV", "breakeven_aov"),
  moneyCol("LT AOV", "long_term_aov"),
  numCol("Qty Mailed", "quantity_mailed", 110),
  numCol("NTF Buyers", "ntf_buyers"),
  pctCol("FE ROR % (LTV)", "fe_ror_ltv", 120),
  moneyCol("FE Cost", "fe_cost"),
  moneyCol("FE CPO", "fe_cpo"),
  moneyCol("FE Purch", "fe_purchases"),
  moneyCol("FE P/L (LTV)", "fe_profit_loss_ltv", 120),
  moneyCol("FE AOV", "fe_aov1"),
  pctCol("FE ROI", "fe_roi"),
  pctCol("Subs %", "subscriptions_percentage"),
  numCol("BE Orders", "be_orders"),
  numCol("BE Mlg Qty", "be_mailed_quantity"),
  pctCol("BE ROR % (LTV)", "be_ror_ltv", 120),
  moneyCol("BE Cost", "be_cost"),
  moneyCol("BE CPO", "be_cpo"),
  moneyCol("BE Purch", "be_purchases"),
  moneyCol("BE P/L (LTV)", "be_profit_loss_ltv", 120),
  moneyCol("BE PL/Buyer (LTV)", "be_profit_loss_per_buyer_ltv", 140),
  pctCol("Reorder Rate (LTV)", "reorder_rate_ltv", 130),
  moneyCol("BE AOV", "be_aov"),
  numCol("Tot Purch", "total_purchases"),
  moneyCol("Tot Cost", "tot_cost"),
  moneyCol("Net P/L", "net_profit_loss_ltv", 120),
  moneyCol("LT AOV", "lt_aov"),
  pctCol("LT ROI", "lt_roi"),
  moneyCol("LTV FE P/L per Buyer", "profit_loss_per_buyer", 160),
  moneyCol("LTV BE P/L per Buyer", "delta", 160),
  moneyCol("LTV Total P/L per Buyer", "profit_loss_per_buyer_total", 170),
  { headerName: "Action", field: "action", width: 120 },
];

// ============================================================
// Default column definition (shared)
// ============================================================
export const defaultColDef = {
  sortable: true,
  filter: true,
  resizable: true,
  minWidth: 80,
};
